import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Building2, Users, Award, Calendar as Cal, TrendingUp, Image as ImgIcon,
  ArrowRight, ArrowUpRight, Sparkles, PlusCircle, UserPlus, FileCheck2, Upload,
  Clock, CircleCheck, CircleAlert, Circle, Inbox, Eye, Download, Info,
  BookOpen, Palette, FileText,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import api, { BACKEND } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { userCan } from "@/lib/rbac";

const CHART_COLORS = ["#4F46E5", "#10B981", "#F59E0B", "#EC4899", "#3B82F6", "#8B5CF6", "#14B8A6"];

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.55, delay: i * 0.05, ease: [0.16, 1, 0.3, 1] } }),
};

const monthLabel = (d) => {
  try {
    const dt = new Date(d);
    if (isNaN(dt)) return null;
    return dt.toLocaleDateString("en-US", { month: "short", year: "2-digit" });
  } catch {
    return null;
  }
};

const buildMonthSeries = (bookings, certificates) => {
  // last 6 months, oldest first
  const now = new Date();
  const months = [];
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push({ key: d.toLocaleDateString("en-US", { month: "short", year: "2-digit" }), bookings: 0, certificates: 0 });
  }
  const map = Object.fromEntries(months.map((m) => [m.key, m]));
  bookings.forEach((b) => {
    const k = monthLabel(b.created_at || b.preferred_date);
    if (k && map[k]) map[k].bookings += 1;
  });
  certificates.forEach((c) => {
    const k = monthLabel(c.created_at || c.date);
    if (k && map[k]) map[k].certificates += 1;
  });
  return months;
};

const buildCategoryDonut = (programs) => {
  const counts = {};
  programs.forEach((p) => {
    const k = p.category || "Other";
    counts[k] = (counts[k] || 0) + 1;
  });
  return Object.entries(counts).map(([name, value]) => ({ name, value }));
};

const buildTimeline = (bookings, certificates) => {
  const items = [];
  bookings.forEach((b) => items.push({
    id: `b-${b.id}`,
    kind: "booking",
    title: `Booking · ${b.school_name || "School"}`,
    subtitle: `${b.teacher_count || 0} teachers · ${b.preferred_date || ""}`,
    status: b.status,
    at: b.created_at,
  }));
  certificates.forEach((c) => items.push({
    id: `c-${c.id}`,
    kind: "certificate",
    title: `Certificate · ${c.teacher_name || "Teacher"}`,
    subtitle: `${c.training_name || ""} · ${c.school_name || ""}`,
    status: c.emailed ? "emailed" : "issued",
    at: c.created_at,
  }));
  return items
    .filter((x) => x.at)
    .sort((a, b) => new Date(b.at) - new Date(a.at))
    .slice(0, 6);
};

const buildUpcoming = (bookings) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return bookings
    .filter((b) => {
      if (!b.preferred_date) return false;
      const d = new Date(b.preferred_date);
      return !isNaN(d) && d >= today && b.status !== "rejected";
    })
    .sort((a, b) => new Date(a.preferred_date) - new Date(b.preferred_date))
    .slice(0, 5);
};

const relativeTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const dateFormat = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  if (isNaN(d)) return String(v);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const StatCard = ({ label, value, icon: Icon, tone, delta, isLoading, testId, to }) => (
  <motion.div
    variants={fadeUp}
    className="group relative rounded-2xl border border-gray-200 bg-white p-6 overflow-hidden hover:border-gray-300 hover:shadow-lg transition-[box-shadow,border-color] duration-200 focus-within:ring-2 focus-within:ring-[#4F46E5] focus-within:ring-offset-2 h-full flex flex-col"
    whileHover={{ y: -3, transition: { duration: 0.18, ease: "easeOut" } }}
    whileTap={{ scale: 0.985, transition: { duration: 0.12 } }}
  >
    <Link
      to={to}
      data-testid={testId}
      aria-label={`${label}: ${isLoading ? "loading" : value}. Open ${label} module.`}
      className="absolute inset-0 z-10 outline-none rounded-2xl"
    />
    <div className={`absolute -top-6 -right-6 w-24 h-24 rounded-full ${tone.blob} blur-2xl opacity-70 pointer-events-none transition-opacity duration-200 group-hover:opacity-100`} />
    <div className="relative flex items-start justify-between">
      <div className={`w-10 h-10 grid place-items-center rounded-xl ${tone.iconBg} transition-transform duration-200 group-hover:scale-105`}>
        <Icon className={`w-5 h-5 ${tone.iconColor}`} strokeWidth={2} />
      </div>
      {delta && !isLoading && (
        <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
          delta.positive ? "text-emerald-700 bg-emerald-50" : "text-rose-700 bg-rose-50"
        }`}>
          <TrendingUp className={`w-3 h-3 ${delta.positive ? "" : "rotate-180"}`} />
          {delta.text}
        </span>
      )}
    </div>
    {isLoading ? (
      <div className="mt-5 h-8 w-20 rounded-md bg-gray-100 animate-pulse relative" data-testid={`${testId}-skeleton`} />
    ) : (
      <div className="mt-5 font-display text-3xl font-semibold tracking-tight text-gray-900 relative leading-none" data-testid={`${testId}-value`}>
        {value.toLocaleString()}
      </div>
    )}
    <div className="mt-2 flex items-center justify-between relative">
      <div className="text-xs text-gray-500 font-medium">{label}</div>
      <ArrowUpRight className="w-3.5 h-3.5 text-gray-300 group-hover:text-[#4F46E5] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-200" />
    </div>
  </motion.div>
);

const StatusPill = ({ s }) => {
  const map = {
    pending: ["bg-amber-50 text-amber-700 border-amber-100", Clock, "Pending"],
    approved: ["bg-emerald-50 text-emerald-700 border-emerald-100", CircleCheck, "Approved"],
    rejected: ["bg-rose-50 text-rose-700 border-rose-100", CircleAlert, "Rejected"],
    completed: ["bg-indigo-50 text-indigo-700 border-indigo-100", CircleCheck, "Completed"],
    emailed: ["bg-indigo-50 text-indigo-700 border-indigo-100", CircleCheck, "Emailed"],
    issued: ["bg-sky-50 text-sky-700 border-sky-100", Award, "Issued"],
  };
  const [cls, Icon, label] = map[s] || ["bg-gray-100 text-gray-700 border-gray-200", Circle, s || "—"];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border ${cls}`}>
      <Icon className="w-3 h-3" /> {label}
    </span>
  );
};

const EmptyState = ({ icon: Icon = Inbox, title, hint }) => (
  <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
    <div className="w-12 h-12 rounded-full bg-gray-50 border border-gray-100 grid place-items-center mb-3">
      <Icon className="w-5 h-5 text-gray-400" />
    </div>
    <div className="text-sm font-medium text-gray-700">{title}</div>
    {hint && <div className="text-xs text-gray-500 mt-1 max-w-xs">{hint}</div>}
  </div>
);

const ErrorState = ({ message = "Something went wrong while loading this section." }) => (
  <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
    <div className="w-12 h-12 rounded-full bg-rose-50 border border-rose-100 grid place-items-center mb-3">
      <CircleAlert className="w-5 h-5 text-rose-500" />
    </div>
    <div className="text-sm font-medium text-gray-700">Couldn&apos;t load data</div>
    <div className="text-xs text-gray-500 mt-1 max-w-xs">{message}</div>
    <button
      onClick={() => window.location.reload()}
      className="mt-3 text-xs font-medium text-[#4F46E5] hover:underline"
      data-testid="error-retry-btn"
    >
      Try again
    </button>
  </div>
);

const SectionCard = ({ title, cta, children, testId }) => (
  <motion.div
    variants={fadeUp}
    className="rounded-2xl border border-gray-200 bg-white overflow-hidden flex flex-col h-full"
    data-testid={testId}
  >
    <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 min-h-[57px]">
      <h2 className="font-display text-base font-semibold text-gray-900 leading-none">{title}</h2>
      {cta}
    </div>
    <div className="flex-1 min-h-[240px]">{children}</div>
  </motion.div>
);

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="rounded-lg bg-[#0F172A] text-white text-xs px-3 py-2 shadow-xl border border-white/10">
      {label && <div className="font-semibold mb-1">{label}</div>}
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: p.color || p.payload.fill }} />
          <span className="capitalize">{p.name}:</span>
          <span className="font-mono font-semibold">{p.value}</span>
        </div>
      ))}
    </div>
  );
};

export default function Overview() {
  const { user, loading: authLoading } = useAuth();
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState({ bookings: true, certs: true, programs: true });
  const [errors, setErrors] = useState({ stats: false, bookings: false, certs: false, programs: false });

  useEffect(() => {
    if (authLoading || !user || user.role !== "admin") return;
    api.get("/admin/stats")
      .then((r) => setStats(r.data))
      .catch(() => { setStats({}); setErrors((e) => ({ ...e, stats: true })); });
    api.get("/bookings")
      .then((r) => setBookings(r.data || []))
      .catch(() => setErrors((e) => ({ ...e, bookings: true })))
      .finally(() => setLoading((l) => ({ ...l, bookings: false })));
    api.get("/certificates")
      .then((r) => setCertificates(r.data || []))
      .catch(() => setErrors((e) => ({ ...e, certs: true })))
      .finally(() => setLoading((l) => ({ ...l, certs: false })));
    api.get("/programs")
      .then((r) => setPrograms(r.data || []))
      .catch(() => setErrors((e) => ({ ...e, programs: true })))
      .finally(() => setLoading((l) => ({ ...l, programs: false })));
  }, [authLoading, user]);

  const s = stats || {};
  const statsLoading = stats === null;
  const chartsLoading = loading.bookings || loading.certs;

  const cards = useMemo(() => ([
    { label: "Schools", value: s.schools ?? 0, icon: Building2, delta: { positive: true, text: "+2 this mo" }, to: "/admin/schools", perm: "schools:read",
      tone: { blob: "bg-indigo-100", iconBg: "bg-indigo-50", iconColor: "text-indigo-600" }, testId: "stat-schools" },
    { label: "Teachers", value: s.teachers ?? 0, icon: Users, delta: null, to: "/admin/teachers", perm: "teachers:read",
      tone: { blob: "bg-emerald-100", iconBg: "bg-emerald-50", iconColor: "text-emerald-600" }, testId: "stat-teachers" },
    { label: "Certificates", value: s.certificates ?? 0, icon: Award, delta: { positive: true, text: `+${s.certificates ?? 0}` }, to: "/admin/certificates", perm: "certificates:read",
      tone: { blob: "bg-amber-100", iconBg: "bg-amber-50", iconColor: "text-amber-600" }, testId: "stat-certificates" },
    { label: "Pending bookings", value: s.bookings_pending ?? 0, icon: Cal, delta: null, to: "/admin/bookings", perm: "bookings:read",
      tone: { blob: "bg-rose-100", iconBg: "bg-rose-50", iconColor: "text-rose-600" }, testId: "stat-pending-bookings" },
    { label: "Programs", value: s.programs ?? 0, icon: TrendingUp, delta: null, to: "/admin/programs", perm: "programs:read",
      tone: { blob: "bg-sky-100", iconBg: "bg-sky-50", iconColor: "text-sky-600" }, testId: "stat-programs" },
    { label: "Gallery albums", value: s.albums ?? 0, icon: ImgIcon, delta: null, to: "/admin/gallery", perm: "gallery:read",
      tone: { blob: "bg-violet-100", iconBg: "bg-violet-50", iconColor: "text-violet-600" }, testId: "stat-gallery-albums" },
  ].filter((c) => !c.perm || userCan(user, c.perm))), [s.schools, s.teachers, s.certificates, s.bookings_pending, s.programs, s.albums, user]);

  const monthSeries = useMemo(() => buildMonthSeries(bookings, certificates), [bookings, certificates]);
  const donutData = useMemo(() => buildCategoryDonut(programs), [programs]);
  const timeline = useMemo(() => buildTimeline(bookings, certificates), [bookings, certificates]);
  const upcoming = useMemo(() => buildUpcoming(bookings), [bookings]);
  const recentCerts = useMemo(
    () => [...certificates].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)).slice(0, 4),
    [certificates]
  );
  const recentBookings = useMemo(
    () => [...bookings].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)).slice(0, 5),
    [bookings]
  );

  const firstName = (user?.name || "").split(" ")[0] || "Admin";

  return (
    <motion.div initial="hidden" animate="show" variants={{ show: { transition: { staggerChildren: 0.06 } } }} className="space-y-6">
      {/* Welcome */}
      <motion.section
        variants={fadeUp}
        className="relative rounded-2xl bg-[#0F172A] text-white p-6 sm:p-8 overflow-hidden"
        data-testid="welcome-card"
      >
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(79,70,229,0.35),transparent_55%),radial-gradient(ellipse_at_bottom_left,rgba(16,185,129,0.22),transparent_55%)]" />
        <div className="absolute inset-0 opacity-[0.05]" style={{ backgroundImage: "radial-gradient(#fff 1px, transparent 1px)", backgroundSize: "16px 16px" }} />
        <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/15 bg-white/5 backdrop-blur text-xs font-medium mb-4">
              <Sparkles className="w-3.5 h-3.5 text-emerald-300" />
              {new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
            </div>
            <h1 className="font-display text-3xl sm:text-4xl tracking-tight font-semibold">
              Welcome back, <span className="bg-gradient-to-r from-indigo-300 via-white to-emerald-300 bg-clip-text text-transparent">{firstName}</span>.
            </h1>
            <p className="mt-2 text-sm text-white/60 max-w-xl">
              Here&apos;s what&apos;s happening across your training operation today.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link
              to="/admin/bookings"
              className="inline-flex items-center gap-1.5 bg-white text-[#0F172A] text-sm font-semibold px-4 py-2.5 rounded-full hover:bg-white/90 transition-all group"
              data-testid="welcome-manage-bookings"
            >
              Manage bookings <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
            </Link>
            <Link
              to="/admin/certificates"
              className="inline-flex items-center gap-1.5 border border-white/20 bg-white/5 backdrop-blur text-white text-sm font-semibold px-4 py-2.5 rounded-full hover:bg-white/10 transition-all"
              data-testid="welcome-generate-cert"
            >
              Generate certificate
            </Link>
          </div>
        </div>
      </motion.section>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        {cards.map((c) => (
          <StatCard key={c.label} {...c} isLoading={statsLoading} />
        ))}
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <SectionCard
          title="Activity · Last 6 months"
          cta={<span className="text-xs text-gray-500 font-medium hidden sm:inline">Bookings & Certificates</span>}
          testId="chart-activity"
        >
          <div className="p-6 h-[280px]">
            {chartsLoading ? (
              <div className="h-full grid grid-cols-6 gap-3 items-end px-2 pb-2" data-testid="chart-activity-skeleton">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="bg-gray-100 rounded-t animate-pulse" style={{ height: `${30 + (i * 11) % 60}%` }} />
                ))}
              </div>
            ) : monthSeries.every((m) => m.bookings === 0 && m.certificates === 0) ? (
              <EmptyState icon={TrendingUp} title="No activity yet" hint="Bookings and certificates from the last 6 months will appear here as they come in." />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={monthSeries} margin={{ top: 8, right: 8, bottom: 0, left: -12 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
                  <XAxis dataKey="key" tick={{ fontSize: 11, fill: "#6B7280" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "#6B7280" }} axisLine={false} tickLine={false} allowDecimals={false} width={28} />
                  <Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(79,70,229,0.05)" }} />
                  <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12, paddingTop: 4 }} />
                  <Bar dataKey="bookings" name="Bookings" fill="#4F46E5" radius={[6, 6, 0, 0]} maxBarSize={28} />
                  <Bar dataKey="certificates" name="Certificates" fill="#10B981" radius={[6, 6, 0, 0]} maxBarSize={28} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </SectionCard>

        <SectionCard title="Programs by category" testId="chart-programs">
          <div className="p-6 h-[280px]">
            {loading.programs ? (
              <div className="h-full grid place-items-center" data-testid="chart-programs-skeleton">
                <div className="w-32 h-32 rounded-full border-8 border-gray-100 border-t-gray-200 animate-spin" />
              </div>
            ) : donutData.length === 0 ? (
              <EmptyState icon={TrendingUp} title="No programs yet" hint="Add programs to see their category distribution." />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
                  <Tooltip content={<ChartTooltip />} />
                  <Pie
                    data={donutData}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={50}
                    outerRadius={82}
                    paddingAngle={3}
                    strokeWidth={0}
                    cx="35%"
                  >
                    {donutData.map((_, i) => (
                      <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Legend
                    iconType="circle"
                    iconSize={7}
                    layout="vertical"
                    align="right"
                    verticalAlign="middle"
                    wrapperStyle={{ fontSize: 11, lineHeight: "16px" }}
                  />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </SectionCard>
      </div>

      {/* Recent bookings + Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <SectionCard
            title="Recent bookings"
            cta={<Link to="/admin/bookings" data-testid="recent-bookings-seeall" className="text-xs font-medium text-[#4F46E5] hover:underline inline-flex items-center gap-1">See all <ArrowUpRight className="w-3 h-3" /></Link>}
            testId="recent-bookings"
          >
            {loading.bookings ? (
              <div className="p-6 space-y-3" data-testid="recent-bookings-skeleton">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="flex items-center gap-4 py-2">
                    <div className="w-10 h-10 rounded-full bg-gray-100 animate-pulse" />
                    <div className="flex-1 space-y-1.5">
                      <div className="h-3 w-40 bg-gray-100 rounded animate-pulse" />
                      <div className="h-2 w-24 bg-gray-100 rounded animate-pulse" />
                    </div>
                    <div className="h-5 w-16 bg-gray-100 rounded-full animate-pulse" />
                  </div>
                ))}
              </div>
            ) : errors.bookings ? (
              <ErrorState message="Bookings feed is temporarily unavailable." />
            ) : recentBookings.length === 0 ? (
              <EmptyState icon={Cal} title="No bookings yet" hint="Schools can request training sessions from the public site." />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wider">
                    <tr>
                      <th className="text-left px-6 py-3 font-medium">School</th>
                      <th className="text-left px-6 py-3 font-medium hidden sm:table-cell">Date</th>
                      <th className="text-left px-6 py-3 font-medium">Teachers</th>
                      <th className="text-left px-6 py-3 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentBookings.map((b, i) => (
                      <motion.tr
                        key={b.id}
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.04, duration: 0.4 }}
                        className="border-t border-gray-100 hover:bg-gray-50/60 active:bg-gray-100/60 transition-colors cursor-pointer focus-within:bg-gray-50"
                        data-testid={`recent-booking-row-${b.id}`}
                      >
                        <td colSpan={4} className="p-0">
                          <Link
                            to="/admin/bookings"
                            data-testid={`recent-booking-open-${b.id}`}
                            className="grid grid-cols-[1fr_auto_auto_auto] gap-x-6 items-center px-6 py-3.5 outline-none focus-visible:ring-2 focus-visible:ring-[#4F46E5] focus-visible:ring-inset"
                            aria-label={`Open booking for ${b.school_name}`}
                          >
                            <span className="font-medium text-gray-900 truncate">{b.school_name}</span>
                            <span className="text-gray-600 hidden sm:inline">{dateFormat(b.preferred_date)}</span>
                            <span className="text-gray-600 tabular-nums">{b.teacher_count}</span>
                            <StatusPill s={b.status} />
                          </Link>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </SectionCard>
        </div>

        <SectionCard title="Quick actions" testId="quick-actions">
          <div className="p-6 grid grid-cols-1 gap-2">
            {[
              { label: "Add school", to: "/admin/schools", icon: Building2, tone: "text-indigo-600 bg-indigo-50", perm: "schools:write" },
              { label: "Add teacher", to: "/admin/teachers", icon: UserPlus, tone: "text-emerald-600 bg-emerald-50", perm: "teachers:write" },
              { label: "Create program", to: "/admin/programs", icon: BookOpen, tone: "text-sky-600 bg-sky-50", perm: "programs:write" },
              { label: "Issue certificate", to: "/admin/certificates", icon: FileCheck2, tone: "text-amber-600 bg-amber-50", perm: "certificates:write" },
              { label: "Upload gallery", to: "/admin/gallery", icon: Upload, tone: "text-violet-600 bg-violet-50", perm: "gallery:write" },
              { label: "Website CMS", to: "/admin/content", icon: Palette, tone: "text-rose-600 bg-rose-50", perm: "cms:write" },
              { label: "Manage bookings", to: "/admin/bookings", icon: PlusCircle, tone: "text-teal-600 bg-teal-50", perm: "bookings:write" },
            ].filter((a) => !a.perm || userCan(user, a.perm)).map(({ label, to, icon: Icon, tone }) => (
              <motion.div key={to} whileTap={{ scale: 0.98 }} transition={{ duration: 0.12 }}>
                <Link
                  to={to}
                  data-testid={`quick-${label.toLowerCase().replace(/\s+/g, '-').replace(/[()]/g, '')}`}
                  className="group flex items-center gap-3 px-4 py-3 rounded-xl border border-gray-200 hover:border-gray-300 hover:bg-gray-50 hover:shadow-sm transition-all outline-none focus-visible:ring-2 focus-visible:ring-[#4F46E5] focus-visible:ring-offset-1"
                >
                  <div className={`w-9 h-9 rounded-lg grid place-items-center ${tone} transition-transform duration-150 group-hover:scale-105`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <span className="text-sm font-medium text-gray-900 flex-1">{label}</span>
                  <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-[#4F46E5] group-hover:translate-x-0.5 transition-all duration-150" />
                </Link>
              </motion.div>
            ))}
          </div>
        </SectionCard>
      </div>

      {/* Widgets row: Certificates + Upcoming + Timeline */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <SectionCard
          title="My certificates"
          cta={<Link to="/admin/certificates" data-testid="my-certs-seeall" className="text-xs font-medium text-[#4F46E5] hover:underline">See all</Link>}
          testId="my-certificates"
        >
          {loading.certs ? (
            <div className="p-6 space-y-3" data-testid="my-certificates-skeleton">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3 py-2">
                  <div className="w-10 h-10 rounded-lg bg-gray-100 animate-pulse" />
                  <div className="flex-1 space-y-1.5">
                    <div className="h-3 w-32 bg-gray-100 rounded animate-pulse" />
                    <div className="h-2 w-20 bg-gray-100 rounded animate-pulse" />
                  </div>
                </div>
              ))}
            </div>
          ) : errors.certs ? (
            <ErrorState message="We couldn&apos;t reach the certificates service." />
          ) : recentCerts.length === 0 ? (
            <EmptyState icon={Award} title="No certificates yet" hint="Once you generate certificates, the latest four will show up here." />
          ) : (
            <ul className="divide-y divide-gray-100">
              {recentCerts.map((c) => (
                <li key={c.id} data-testid={`cert-item-${c.id}`} className="px-6 py-3.5 hover:bg-gray-50/60 transition-colors group">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-100 to-amber-50 grid place-items-center shrink-0">
                      <Award className="w-4 h-4 text-amber-600" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-gray-900 truncate">{c.teacher_name}</div>
                      <div className="text-xs text-gray-500 truncate">{c.training_name} · {c.school_name}</div>
                      <div className="mt-1 flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-[10px] text-gray-500">{c.certificate_id}</span>
                        <StatusPill s={c.emailed ? "emailed" : "issued"} />
                      </div>
                      <div className="mt-2.5 flex items-center gap-1.5 text-xs">
                        <a
                          href={`/verify/${c.certificate_id}`}
                          target="_blank"
                          rel="noreferrer"
                          data-testid={`cert-preview-${c.id}`}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-gray-50 hover:bg-gray-100 active:bg-gray-200 text-gray-700 font-medium transition-colors outline-none focus-visible:ring-2 focus-visible:ring-[#4F46E5]"
                          aria-label={`Preview certificate for ${c.teacher_name}`}
                        >
                          <Eye className="w-3 h-3" /> Preview
                        </a>
                        <a
                          href={`${BACKEND}/api/certificates/${c.certificate_id}/pdf`}
                          target="_blank"
                          rel="noreferrer"
                          data-testid={`cert-download-${c.id}`}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-amber-50 hover:bg-amber-100 active:bg-amber-200 text-amber-700 font-medium transition-colors outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
                          aria-label={`Download PDF for ${c.teacher_name}`}
                        >
                          <Download className="w-3 h-3" /> Download
                        </a>
                        <Link
                          to="/admin/certificates"
                          data-testid={`cert-details-${c.id}`}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-gray-50 hover:bg-gray-100 active:bg-gray-200 text-gray-700 font-medium transition-colors outline-none focus-visible:ring-2 focus-visible:ring-[#4F46E5]"
                          aria-label={`View details for ${c.teacher_name}`}
                        >
                          <Info className="w-3 h-3" /> Details
                        </Link>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </SectionCard>

        <SectionCard
          title="Upcoming trainings"
          cta={<Link to="/admin/bookings" data-testid="upcoming-seeall" className="text-xs font-medium text-[#4F46E5] hover:underline">See all</Link>}
          testId="upcoming-trainings"
        >
          {loading.bookings ? (
            <div className="p-6 space-y-3" data-testid="upcoming-trainings-skeleton">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3 py-2">
                  <div className="w-11 h-11 rounded-lg bg-gray-100 animate-pulse" />
                  <div className="flex-1 space-y-1.5">
                    <div className="h-3 w-32 bg-gray-100 rounded animate-pulse" />
                    <div className="h-2 w-24 bg-gray-100 rounded animate-pulse" />
                  </div>
                </div>
              ))}
            </div>
          ) : errors.bookings ? (
            <ErrorState message="Upcoming schedule is temporarily unavailable." />
          ) : upcoming.length === 0 ? (
            <EmptyState icon={Cal} title="No upcoming trainings" hint="Bookings scheduled in the future will appear here." />
          ) : (
            <ul className="divide-y divide-gray-100">
              {upcoming.map((b) => {
                const d = new Date(b.preferred_date);
                const day = d.getDate();
                const mon = d.toLocaleDateString("en-US", { month: "short" });
                return (
                  <li key={b.id} data-testid={`upcoming-item-${b.id}`} className="group hover:bg-gray-50/60 transition-colors">
                    <Link
                      to="/admin/bookings"
                      data-testid={`upcoming-open-${b.id}`}
                      className="flex items-start gap-3 px-6 py-3.5 outline-none focus-visible:ring-2 focus-visible:ring-[#4F46E5] focus-visible:ring-inset"
                      aria-label={`Open training for ${b.school_name} on ${b.preferred_date}`}
                    >
                      <div className="w-11 h-12 rounded-lg bg-gradient-to-br from-indigo-50 to-white border border-indigo-100 grid place-items-center shrink-0 transition-transform duration-150 group-hover:scale-105">
                        <div className="text-[9px] font-semibold text-indigo-600 uppercase tracking-wider">{mon}</div>
                        <div className="text-base font-display font-bold text-[#0F172A] leading-none -mt-1">{day}</div>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold text-gray-900 truncate">{b.school_name}</div>
                        <div className="text-xs text-gray-500 truncate">{b.teacher_count} teachers · {b.city || "—"}</div>
                        <div className="mt-1 flex items-center gap-2">
                          <StatusPill s={b.status} />
                        </div>
                        <div className="mt-2 flex items-center gap-1.5 text-xs opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity duration-150">
                          <span
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-gray-50 hover:bg-gray-100 active:bg-gray-200 text-gray-700 font-medium transition-colors"
                            data-testid={`upcoming-view-${b.id}`}
                          >
                            <Eye className="w-3 h-3" /> View
                          </span>
                          <span
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-indigo-50 hover:bg-indigo-100 active:bg-indigo-200 text-indigo-700 font-medium transition-colors"
                            data-testid={`upcoming-edit-${b.id}`}
                          >
                            <FileText className="w-3 h-3" /> Edit
                          </span>
                          <span
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-50 hover:bg-emerald-100 active:bg-emerald-200 text-emerald-700 font-medium transition-colors"
                            data-testid={`upcoming-attendance-${b.id}`}
                          >
                            <Users className="w-3 h-3" /> Attendance
                          </span>
                        </div>
                      </div>
                      <ArrowUpRight className="w-4 h-4 text-gray-300 shrink-0 mt-1 group-hover:text-[#4F46E5] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-150" />
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </SectionCard>

        <SectionCard title="Recent activity" testId="recent-activity">
          {chartsLoading ? (
            <div className="p-6 space-y-3" data-testid="recent-activity-skeleton">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3 py-1.5">
                  <div className="w-8 h-8 rounded-full bg-gray-100 animate-pulse" />
                  <div className="flex-1 space-y-1.5">
                    <div className="h-3 w-40 bg-gray-100 rounded animate-pulse" />
                    <div className="h-2 w-16 bg-gray-100 rounded animate-pulse" />
                  </div>
                </div>
              ))}
            </div>
          ) : (errors.bookings && errors.certs) ? (
            <ErrorState message="Activity feed will return once the connection is restored." />
          ) : timeline.length === 0 ? (
            <EmptyState icon={Sparkles} title="Nothing to show yet" hint="Bookings and certificate activity will surface here in real time." />
          ) : (
            <ol className="relative p-6">
              <div className="absolute left-[30px] top-6 bottom-6 w-px bg-gray-200" />
              {timeline.map((t, i) => {
                const to = t.kind === "certificate" ? "/admin/certificates" : "/admin/bookings";
                return (
                  <motion.li
                    key={t.id}
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.05, duration: 0.4 }}
                    className="relative pl-9 pb-4 last:pb-0"
                    data-testid={`activity-${t.id}`}
                  >
                    <div className={`absolute left-[18px] top-1 w-6 h-6 rounded-full grid place-items-center border-2 border-white shadow z-10 ${
                      t.kind === "certificate" ? "bg-amber-500" : "bg-indigo-500"
                    }`}>
                      {t.kind === "certificate"
                        ? <Award className="w-3 h-3 text-white" />
                        : <Cal className="w-3 h-3 text-white" />}
                    </div>
                    <Link
                      to={to}
                      data-testid={`activity-open-${t.id}`}
                      className="group flex items-start justify-between gap-2 -mx-1.5 px-1.5 py-1 rounded-md hover:bg-gray-50 active:bg-gray-100 transition-colors outline-none focus-visible:ring-2 focus-visible:ring-[#4F46E5]"
                      aria-label={`Open ${t.title}`}
                    >
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate group-hover:text-[#0F172A]">{t.title}</div>
                        <div className="text-xs text-gray-500 truncate">{t.subtitle}</div>
                      </div>
                      <span className="text-[10px] text-gray-400 font-mono shrink-0 whitespace-nowrap mt-0.5 flex items-center gap-1">
                        {relativeTime(t.at)}
                        <ArrowUpRight className="w-3 h-3 text-transparent group-hover:text-[#4F46E5] transition-colors" />
                      </span>
                    </Link>
                  </motion.li>
                );
              })}
            </ol>
          )}
        </SectionCard>
      </div>
    </motion.div>
  );
}

export { StatusPill };
